import { OHT_MODEL_SCALE_MULTIPLIER } from '../../../utils/layout3d';

/**
 * Shared types for the procedural AMHS 3D kit (default layout stocker / equipment).
 */

export type EquipmentStatus = 'IDLE' | 'RUNNING' | 'ERROR' | 'WARNING' | 'OFFLINE';

export type AMHSEquipmentType =
  | 'LOAD_PORT'
  | 'CONVEYOR'
  | 'AMHS_INTERFACE';

export const STATUS_LED_COLOR: Record<EquipmentStatus, string> = {
  /** Idle — cool pale blue. */
  IDLE: '#CFE3FF',
  /** Processing FOUP — single teal technology accent. */
  RUNNING: '#31C8C8',
  /** Small warm status light. */
  ERROR: '#E8A84E',
  WARNING: '#E8A84E',
  /** Offline / FOUP out — deep violet. */
  OFFLINE: '#6B5E8C',
};

/**
 * Every equipment surface — cabinet, panels, covers, plinth, trim — is this one
 * metallic blue; form comes from the brushed shading in `AMHS_BODY_SHADING`, not
 * from colour breaks. Stockers keep their cyan family and the floor stays gray.
 */
export const EQUIPMENT_BLUE = '#173B61';

/**
 * Reference process-tool cabinet colours. Shared with the instanced overview
 * silhouette so a tool keeps the same colours whichever detail level draws it.
 */
export const PROCESS_CABINET_PALETTE = {
  navy: '#202A32',
  navyDeep: '#111820',
  beige: '#356890',
  beigeDoor: '#4A82AE',
  beigeShade: '#284868',
  top: '#4A82AE',
  cyan: '#2FE6D4',
  amber: '#E8B45A',
  silver: '#5C96BE',
  ready: '#3DCC5A',
  busy: '#E8C44A',
  error: '#E24B4B',
  estop: '#D62828',
  estopRing: '#F0D020',
  dim: '#6B6560',
};

/**
 * Cleanroom palette — every equipment surface carries a hue so families stay
 * apart on the floor. Only the floor itself stays neutral gray.
 */
export const AMHS_PALETTE = {
  /** Stocker frame — clean neutral gray. */
  stockerFrame: '#94A3B8',
  /** Stocker base skids — deeper neutral gray under the frame. */
  stockerFrameDark: '#475569',
  /** Rack interior — dark neutral slate. */
  stockerAccent: '#1E293B',
  /** Shelves — dark neutral gray. */
  stockerBeam: '#334155',
  /**
   * FOUPs stored in the rack. Slightly lifted black so cassettes remain
   * readable against the dark teal interior.
   */
  stockerBin: '#151A1F',
  /** 3D FOUP shells — single uniform color across all stocker FOUPs. */
  stockerRawFoup: '#0B0D10',
  stockerProcessedFoup: '#0B0D10',
  /** LED illumination — blue in place of the old champagne. */
  ledChampagne: '#F6C86B',
  ledWarmWhite: '#FFF2CB',
  /** Structural metal — steel blue rather than bare silver. */
  metal: '#9CB0BF',
  metalDark: '#0E2945',
  /** Trim — same metallic blue as everything else on the machine. */
  metalLight: '#E8EEF1',
  /** Process-tool body. */
  body: EQUIPMENT_BLUE,
  bodyWarm: '#244E77',
  bodyAccent: '#0E2945',
  /** Recessed load-port chamber interior. */
  chamber: EQUIPMENT_BLUE,
  amhsBlue: EQUIPMENT_BLUE,
  amhsBlueDark: EQUIPMENT_BLUE,
  /** Single technology accent — status / indicator use only. */
  amhsBlueBright: '#31C8C8',
  accentTeal: '#31C8C8',
  accentAqua: '#7DDEDA',
  panel: '#214B74',
  tray: '#7FA7B9',
  traySilver: '#D9E3E6',
  /** Raw FOUP shell — dark blue metallic cleanroom polymer. */
  foup: '#0F2B5C',
  foupLid: '#081730',
  foupDetail: '#0A1C36',
  /** Processed FOUP shell — single uniform dark blue metallic color across all FOUPs. */
  foupProcessed: '#0F2B5C',
  /** Equipment plinth. */
  base: '#102842',
  baseRail: '#0A1C31',
} as const;

export type FoupKind = 'raw' | 'processed';

export function resolveFoupKindColor(_kind: FoupKind = 'raw'): string {
  return AMHS_PALETTE.foup; // '#0F2B5C' (Dark Blue Metallic)
}

/**
 * Brushed-metal shading for equipment bodies — a satin highlight without
 * losing the paint colour. The scene lights everything with Three's white
 * RoomEnvironment (see SceneEnvironment3D), so pushing metalness any higher
 * drops the diffuse term and the cabinets wash out to white room reflections;
 * the damped envMapIntensity keeps that reflection from flooding the hue.
 */
export const AMHS_BODY_SHADING = {
  metalness: 0.5,
  roughness: 0.28,
  envMapIntensity: 0.5,
} as const;

/**
 * Load-port hardware (decks, kerbs, dock seats, shutters) sits nearly flat and
 * faces up into the RoomEnvironment, so any metalness there mirrors the room
 * back at the camera as a white blowout. Keep these surfaces matte.
 */
export const AMHS_LOAD_PORT_SHADING = {
  metalness: 0.04,
  roughness: 0.95,
  envMapIntensity: 0.12,
} as const;

/** Body colours for one equipment family. */
export type AmhsEquipmentTint = {
  body: string;
  panel: string;
  base: string;
  rail: string;
  /** Covers, pads and caps. */
  cap: string;
};

export type AmhsEquipmentFamily = 'tool' | 'buffer' | 'lifter' | 'loadPort';

/**
 * Every family, and every surface within it, is the one metallic blue.
 * Families are told apart by silhouette, not colour.
 */
const SOLID_BLUE: AmhsEquipmentTint = {
  body: EQUIPMENT_BLUE,
  panel: '#214B74',
  base: '#102842',
  rail: '#0A1C31',
  cap: '#E9EFF2',
};

export const AMHS_EQUIPMENT_TINTS: Record<AmhsEquipmentFamily, AmhsEquipmentTint> = {
  tool: SOLID_BLUE,
  buffer: SOLID_BLUE,
  lifter: SOLID_BLUE,
  loadPort: SOLID_BLUE,
};

/**
 * Per-family ambient accent. Each element type gets its own glow colour so
 * tools, stockers and FOUP transfers stay distinguishable at a glance instead
 * of every object washing out into the same champagne.
 */
export const AMHS_AMBIENT = {
  /** Process tools / load ports — bright blue. */
  equipment: { key: '#EAB85D', soft: '#FFF0C7' },
  /** Stockers — neutral gray/silver glow. */
  stocker: { key: '#94A3B8', soft: '#CBD5E1' },
  /** Conveyors and transfer decks — teal. */
  transfer: { key: '#31C8C8', soft: '#6FE3E3' },
} as const;

export type Vec3 = [number, number, number];

/** Kit FOUP mesh nominal width (see `FOUP.tsx`). */
export const KIT_FOUP_BASE_WIDTH = 0.4;

/** Kit FOUP mesh nominal height before uniform scale (see `FOUP.tsx`). */
export const KIT_FOUP_BASE_HEIGHT = 0.4;

/** Shared boost so OHT, stocker, and equipment FOUPs read clearly at fab scale. */
export const KIT_FOUP_VISUAL_BOOST = 1.25;

/**
 * Same world size as the OHT `FoupBox3D` carrier — used for port, crane, rack,
 * and equipment interior so FOUP never shrinks between drop / carry / store.
 */
export function resolveKitFoupScale(
  _width?: number,
  _height?: number,
  _depth?: number
): number {
  void _width;
  void _height;
  void _depth;
  const S = OHT_MODEL_SCALE_MULTIPLIER;
  // Mirrors OhtVehicle3DModel FoupBox3D: boxW = 8*0.42*S - 0.22*S
  const ohtBoxW = 8.0 * 0.42 * S - 0.22 * S;
  return Math.max(4.5, ohtBoxW / KIT_FOUP_BASE_WIDTH) * KIT_FOUP_VISUAL_BOOST;
}

/** World-space FOUP height after kit scale — used for hoist / deck alignment. */
export function resolveKitFoupWorldHeight(
  scale: number = resolveKitFoupScale()
): number {
  return KIT_FOUP_BASE_HEIGHT * scale;
}

/** Instanced FOUP proxy dimensions — matches OHT carrier and rack `InstancedStockerFoups`. */
export function kitFoupProxyDimensions(kitScale: number): {
  width: number;
  height: number;
  depth: number;
} {
  return {
    width: KIT_FOUP_BASE_WIDTH * kitScale,
    height: 0.36 * kitScale,
    depth: 0.38 * kitScale,
  };
}

/**
 * Rack FOUP scale — always matches OHT / load-port kit size so a pod never
 * shrinks when it moves from the crane into a shelf slot.
 */
export function resolveStockerRackFoupScale(
  _trayW?: number,
  _trayD?: number,
  _slotH?: number,
  _trayH = 0
): number {
  void _trayW;
  void _trayD;
  void _slotH;
  void _trayH;
  return resolveKitFoupScale();
}

/** FOUP bottom sits on shelf deck top (slot positions are deck-centre Y). */
export function resolveStockerRackFoupY(
  slotDeckCenterY: number,
  trayH: number,
  foupHeight: number,
  mode: 'mesh' | 'instanced'
): number {
  const deckTop = slotDeckCenterY + trayH * 0.5;
  if (mode === 'instanced') return deckTop + foupHeight * 0.5;
  // StockerFoup `position` = pod foot on deck top.
  return deckTop;
}
